// src/pages/CoursesPage.js
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./CoursesPage.css";
import { NeuralNoise } from "../components/ui/neural-noise";

const CoursesPage = () => {
  const navigate = useNavigate();

  /* ================= PARALLAX ================= */
  const onlineImgRef = useRef(null);
  const offlineImgRef = useRef(null);

  useEffect(() => {
    const onlineEl = document.querySelector(".glow-online");
    const offlineEl = document.querySelector(".glow-offline");
    if (!onlineEl || !offlineEl) return;

    const handleMove = (e, imgRef, wrapperEl) => {
      if (!imgRef.current || !wrapperEl) return;
      const rect = wrapperEl.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      imgRef.current.style.transform = `scale(1.06) translate(${x * 0.02}px, ${y * 0.02}px)`;
    };

    const reset = (imgRef) => {
      if (imgRef.current) imgRef.current.style.transform = "scale(1)";
    };

    const onOnlineMove = (e) => handleMove(e, onlineImgRef, onlineEl);
    const onOfflineMove = (e) => handleMove(e, offlineImgRef, offlineEl);
    const onOnlineLeave = () => reset(onlineImgRef);
    const onOfflineLeave = () => reset(offlineImgRef);

    onlineEl.addEventListener("mousemove", onOnlineMove);
    offlineEl.addEventListener("mousemove", onOfflineMove);
    onlineEl.addEventListener("mouseleave", onOnlineLeave);
    offlineEl.addEventListener("mouseleave", onOfflineLeave);

    return () => {
      onlineEl.removeEventListener("mousemove", onOnlineMove);
      offlineEl.removeEventListener("mousemove", onOfflineMove);
      onlineEl.removeEventListener("mouseleave", onOnlineLeave);
      offlineEl.removeEventListener("mouseleave", onOfflineLeave);
    };
  }, []);

  /* ================= FAQ ================= */
  const [activeIndex, setActiveIndex] = useState(null);

  const faqData = [
    {
      question: "What is Crafting Brain?",
      answer:
        "Crafting Brain is a learning platform focused on AI, Data Science, and software skills through real-world projects, mentorship, and career support.",
    },
    {
      question: "Who can use Crafting Brain?",
      answer:
        "Students, fresh graduates, working professionals, and career switchers can use Crafting Brain to upskill or transition careers.",
    },
    {
      question: "What types of courses are available?",
      answer:
        "We offer online and offline programs including AI, Gen-AI, Data Engineering, Analytics, and focused bootcamps.",
    },
    {
      question: "Are the courses beginner-friendly?",
      answer:
        "Yes. Courses start from fundamentals and gradually move to advanced concepts with guided mentorship.",
    },
    {
      question: "Do you provide certificates and placement assistance?",
      answer:
        "Yes. Selected programs include industry-recognized certificates and placement assistance.",
    },
    {
      question: "Can I access Crafting Brain from my phone or tablet?",
      answer:
        "Yes. Our platform works seamlessly across mobile, tablet, and desktop devices.",
    },
  ];

  /* ================= COURSE DATA ================= */
  const onlineCourses = useMemo(
    () => [
      {
        id: 1,
        slug: "advanced-ai-agentic-systems",
        image:
          "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&fit=crop",
        category: "ARTIFICIAL INTELLIGENCE",
        title: "Advanced Artificial Intelligence & Agentic Systems Program",
        duration: "(12–14 weeks / 3–3.5 months)",
        details:
          "Machine Learning, Deep Learning, NLP, Transformers, LLMs, Agentic AI",
        stipend: "₹5,000",
      },
      {
        id: 2,
        slug: "gen-ai-professional",
        image:
          "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&fit=crop",
        category: "GEN-AI PROFESSIONAL",
        title: "Gen-AI Professional Course – Complete Package",
        duration: "(24–25 weeks / 6–6.5 months)",
        details:
          "Advanced Data Engineering, Data Analysis, Data Science & Gen-AI",
        stipend: "₹10,000",
      },
      {
        id: 3,
        slug: "data-engineering-analytics",
        image:
          "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&fit=crop",
        category: "ADVANCED DATA ANALYSIS",
        title: "Advanced Data Engineering & Analytics Program",
        duration: "(15–16 weeks / 3.5–4 months)",
        details:
          "Python, Data Engineering, Cloud, SQL, Advanced Analytics, ML-1, Tableau",
        stipend: "₹3,000",
      },
    ],
    []
  );

  const offlineCourses = useMemo(
    () => [
      {
        id: 4,
        slug: "python-data-engineering",
        image:
          "https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=800&fit=crop",
        category: "DATA ENGINEERING",
        title: "Python Programming & Data Engineering Bootcamp",
        duration: "(6–7 weeks / 1.5–2 months)",
        details: "Python fundamentals + Data Engineering principles",
        stipend: "No Stipend",
      },
      {
        id: 5,
        slug: "sql-data-analysis",
        image:
          "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&fit=crop",
        category: "DATA ANALYSIS",
        title: "SQL Mastery & Advanced Data Analysis Bootcamp",
        duration: "(4–5 weeks / 1–1.5 months)",
        details: "SQL fundamentals to advanced + data analysis",
        stipend: "No Stipend",
      },
      {
        id: 6,
        slug: "ml-deep-learning",
        image:
          "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&fit=crop",
        category: "MACHINE LEARNING",
        title: "Advanced Machine Learning & Deep Learning Sprint",
        duration: "(9–10 weeks / 2.5–3 months)",
        details: "ML + Deep Learning advanced concepts",
        stipend: "No Stipend",
      },
    ],
    []
  );

  // ✅ NEW: single list for showing all courses together (no online/offline split below)
  const allCourses = useMemo(
    () => [...onlineCourses, ...offlineCourses],
    [onlineCourses, offlineCourses]
  );

  /* ================= GUIDED REGISTRATION ================= */
  const [interest, setInterest] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const interests = [
    "Artificial Intelligence",
    "Gen-AI",
    "Data Engineering",
    "Data Analysis",
    "Machine Learning",
    "Not Sure",
  ];

  /* ================= THEME -> NeuralNoise ================= */
  const [theme, setTheme] = useState(
    document?.documentElement?.getAttribute("data-theme") || "dark"
  );

  useEffect(() => {
    const el = document.documentElement;
    const update = () => setTheme(el.getAttribute("data-theme") || "dark");
    update();

    const obs = new MutationObserver(update);
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  const noiseColor = theme === "light" ? [0.12, 0.35, 1.0] : [1.0, 0.65, 0.12];
  const noiseOpacity = theme === "light" ? 0.42 : 0.78;

  return (
    <div className="courses-page">
      {/* ✅ Neural background */}
      <NeuralNoise
        color={noiseColor}
        opacity={noiseOpacity}
        speed={0.00035}
        className="cpNoise"
      />

      {/* ✅ Keep all content above */}
      <div className="cpContent">
        {/* HERO */}
        <section className="hero-section">
          <div className="hero-container">
            <h1 className="hero-title">
              <span className="hero-title-main">
                Relearn empowers your growth —
              </span>
              <span className="text-gradient"> anytime, anywhere.</span>
            </h1>
            <h3 className="hero-subtitle">
              Learn in-demand tech skills with real-world projects, mentorship,
              and placement support.
            </h3>
          </div>
        </section>

        {/* STIPEND / EXPERIENCE / PLACEMENT */}
        <section className="features-strip">
          <div className="features-wrapper">
            <div className="feature-card side-card">
              <div className="feature-icon-circle">🎓</div>
              <h2>Experience Letter from INIKOLA</h2>
              <p>Opportunity to work on AI home automation products</p>
            </div>

            <div className="feature-card center-card">
              <div className="feature-icon-glow">💰</div>
              <h2>Stipend up to ₹10k every month</h2>
              <p>Performance-based rewards</p>
            </div>

            <div className="feature-card side-card">
              <div className="feature-icon-circle">🎯</div>
              <h2>Placement assistance</h2>
              <p>50–100 job applications everyday</p>
            </div>
          </div>
        </section>

        {/* ✅ KEEP TOP ONLINE & OFFLINE INTRO SECTION */}
        <section className="intro-section">
          <div className="courses-vertical-wrapper">
            {/* ONLINE */}
            <div className="course-row">
              <div className="course-card-column">
                <div className="intro-card glow-online">
                  <h2>Online Courses</h2>
                  <p>Learn from anywhere with expert-led programs</p>

                  <div className="intro-visual">
                    <img
                      ref={onlineImgRef}
                      src="https://images.unsplash.com/photo-1619852182277-79aa23f82c8e?w=900&fit=crop"
                      alt="Online"
                    />
                    <div className="hover-preview">
                      <span className="course-pill">
                        Advanced Artificial Intelligence & Agentic Systems
                        Program
                      </span>
                      <span className="course-pill">
                        Gen-AI Professional Course – Complete Package
                      </span>
                      <span className="course-pill">
                        Advanced Data Engineering & Analytics Program
                      </span>
                    </div>
                  </div>

                  <div className="course-card-actions">
                    <button
                      className="view-courses-btn online-btn"
                      type="button"
                      onClick={() =>
                        document
                          .querySelector("#our-courses")
                          ?.scrollIntoView({ behavior: "smooth" })
                      }
                    >
                      View Courses →
                    </button>

                    <button
                      className="register-btn"
                      type="button"
                      onClick={() => navigate("/courseregister")}
                    >
                      Register
                    </button>
                  </div>
                </div>
              </div>

              <div className="course-description-column">
                <h3>Online Learning Benefits</h3>
                <p>
                  Learn flexibly with live mentor-led sessions, recorded content,
                  hands-on industry projects, and continuous evaluation.
                </p>
                <ul>
                  <li>Live interactive classes</li>
                  <li>Recorded sessions for revision</li>
                  <li>Industry-level projects</li>
                  <li>Placement-oriented curriculum</li>
                </ul>
              </div>
            </div>

            {/* OFFLINE */}
            <div className="course-row">
              <div className="course-card-column">
                <div className="intro-card glow-offline">
                  <h2>Offline Courses</h2>
                  <p>Hands-on classroom learning with mentors</p>

                  <div className="intro-visual">
                    <img
                      ref={offlineImgRef}
                      src="https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=900&fit=crop"
                      alt="Offline"
                    />
                    <div className="hover-preview">
                      <span className="course-pill">
                        Python Programming & Data Engineering Bootcamp
                      </span>
                      <span className="course-pill">
                        SQL Mastery & Advanced Data Analysis Bootcamp
                      </span>
                      <span className="course-pill">
                        Advanced Machine Learning & Deep Learning Sprint
                      </span>
                    </div>
                  </div>

                 <div className="course-card-actions">
                            <button
                              className="view-courses-btn offline-btn"
                              type="button"
                              onClick={() => navigate("/offline-genai")}
                            >
                              Learn More →
                            </button>

                            <button
                              className="register-btn"
                              type="button"
                              onClick={() => navigate("/offline-genai")}
                            >
                              Register
                            </button>
                          </div>

                </div>
              </div>

              <div className="course-description-column">
                <h3>Offline Learning Benefits</h3>
                <p>
                  Experience structured classroom learning with expert trainers,
                  lab access, peer collaboration, and daily mentoring.
                </p>
                <ul>
                  <li>In-person expert guidance</li>
                  <li>Dedicated lab infrastructure</li>
                  <li>Daily practice & doubt sessions</li>
                  <li>Campus placement support</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* GUIDED REGISTRATION (kept same) */}
        <section className="guided-registration">
          <div className="guided-card">
            <h2>🎓 Not sure which course is right for you?</h2>
            <p>Get free 1-on-1 guidance from our experts before enrolling.</p>

            <div className="guided-interests">
              {interests.map((item) => (
                <button
                  key={item}
                  className={`guided-pill ${interest === item ? "active" : ""}`}
                  onClick={() => {
                    setInterest(item);
                    setSubmitted(false);
                  }}
                  type="button"
                >
                  {item}
                </button>
              ))}
            </div>

            {interest && !submitted && (
              <form
                className="guided-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  setSubmitted(true);
                }}
              >
                <input type="text" placeholder="Full Name" required />
                <input type="email" placeholder="Email Address" required />
                <input type="tel" placeholder="Phone Number" required />
                <input type="text" placeholder="City" required />

                <select required defaultValue="">
                  <option value="" disabled>
                    Current Occupation
                  </option>
                  <option>Student</option>
                  <option>Working Professional</option>
                  <option>Fresher</option>
                  <option>Career Switcher</option>
                  <option>Other</option>
                </select>

                <select required defaultValue="">
                  <option value="" disabled>
                    Preferred Mode
                  </option>
                  <option>Online</option>
                  <option>Offline</option>
                </select>

                <button type="submit">Get Free Course Guidance</button>
              </form>
            )}

            {submitted && (
              <div className="guided-success">
                ✅ Our advisor will contact you within 24 hours.
              </div>
            )}
          </div>
        </section>

        {/* ✅ ONLY CHANGE: ONE COURSES SECTION (NO ONLINE/OFFLINE SPLIT HERE) */}
        <section id="our-courses" className="courses-section">
          <h2 className="section-title">Our Courses</h2>
          <div className="courses-grid">
            {allCourses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                onClick={() =>
                  navigate(`/enroll/${course.slug}`, { state: { course } })
                }
              />
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="faq-section">
          <div className="faq-wrapper">
            <div className="faq-left">
              <span className="faq-badge">? NEED HELP</span>

              <h2 className="faq-title">
                Frequently Asked <br />
                <span>Questions</span>
              </h2>

              <div className="faq-divider" />

              <p className="faq-description">
                We're here to make learning easy. Explore our FAQs to quickly find
                the information you need.
              </p>

              <div className="faq-support-card">
                <h4>Still have a question?</h4>
                <p>If you need more information, feel free to reach out.</p>
                <button className="faq-ask-btn" type="button">
                  Ask a Question →
                </button>
              </div>
            </div>

            <div className="faq-right">
              {faqData.map((item, index) => {
                const isOpen = activeIndex === index;
                return (
                  <div key={index} className={`faq-item ${isOpen ? "active" : ""}`}>
                    <button
                      type="button"
                      className="faq-question-row"
                      onClick={() => setActiveIndex(isOpen ? null : index)}
                    >
                      <span className="faq-question-text">{item.question}</span>
                      <span className={`faq-icon ${isOpen ? "open" : ""}`}>
                        {isOpen ? "−" : "+"}
                      </span>
                    </button>

                    <div className={`faq-answer ${isOpen ? "open" : ""}`}>
                      <p>{item.answer}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

/* COURSE CARD */
const CourseCard = ({ course, onClick }) => (
  <div className="course-card" role="button" tabIndex={0} onClick={onClick}>
    <div className="course-image">
      <img src={course.image} alt={course.title} />
      <span className="course-badge">{course.category}</span>
    </div>

    <div className="course-content">
      <h3 className="course-title">{course.title}</h3>
      <p className="duration">{course.duration}</p>
      <p className="course-details">{course.details}</p>

      <div className="course-footer">
        <span className="stipend">{course.stipend}</span>
        <button
          className="join-btn"
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          type="button"
        >
          Course Details →
        </button>
      </div>
    </div>
  </div>
);

export default CoursesPage;
